/**
 * @module BewerberStatusPage
 * @description Public page for applicants to check their application status.
 * @route /bewerber/status/:token (public, no auth)
 * @since v2.241.0
 * #959 Bundle B: status-driven (7-Werte-SSoT) statt pipeline_stufe-Lookup.
 * @updated v2.846.0 — #1779: WeShip-Logo, i18n (de/en), Du-Ansprache bestaetigt.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const f = (url) => fetch(`${API}${url}`);

const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

// Canonical status order for stepper (terminal states handled separately)
const STATUS_FLOW = ['beworben', 'in_review', 'eingeladen', 'zugesagt', 'aktiv'];
const STATUS_FALLBACK = {
  beworben: 'Bewerbung eingegangen',
  in_review: 'In Prüfung',
  eingeladen: 'Eingeladen',
  zugesagt: 'Zugesagt',
  aktiv: 'Aktiv',
  abgelehnt: 'Abgelehnt',
  inaktiv: 'Inaktiv',
};

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

export default function BewerberStatusPage() {
  const { token } = useParams();
  const { t, language, setLanguage } = useLanguage();
  useEffect(() => { if (language !== 'de') setLanguage('de'); }, []); // DE als Standard (wie /karriere)
  const statusLabel = (k) => t(`bewerberstatus.status.${k}`, STATUS_FALLBACK[k] || k);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    f(`/api/public/recruiting/status/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error('not_found');
        return r.json();
      })
      .then(setData)
      .catch(() => setError('not_found'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div className="text-center">
          <img src="/weship-logo.png" alt="WeShip Fulfillment" className="h-10 w-auto mx-auto mb-4" />
          <h1 className="text-lg font-bold text-[#1A1A2E] mb-2">{t('bewerberstatus.fehler.titel', 'Status nicht verfügbar')}</h1>
          <p className="text-sm text-[#6B7280]">{t('bewerberstatus.fehler.text', 'Der angefragte Bewerbungsstatus konnte nicht gefunden werden.')}</p>
        </div>
      </div>
    );
  }

  const status = data.status || 'beworben';
  const isTerminal = status === 'abgelehnt' || status === 'inaktiv';
  const currentIdx = STATUS_FLOW.indexOf(status);
  const stepperStatus = isTerminal ? STATUS_FLOW : STATUS_FLOW;

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--border-default,#E5E7EB)]">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-3">
          <img src="/weship-logo.png" alt="WeShip Fulfillment" className="h-9 w-auto flex-shrink-0" />
          <div>
            <h1 className="text-base font-bold text-[#1A1A2E]">{t('bewerberstatus.titel', 'Bewerbungsstatus')}</h1>
            <p className="text-xs text-[#6B7280]">WeShip Logistik GmbH</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Name Card */}
        <div className="bg-white rounded-xl border border-[var(--border-default,#E5E7EB)] p-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
              style={{ background: data.bg_farbe || '#EBF4FD', color: data.farbe || '#338edc' }}>
              {data.vorname?.[0] || '?'}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#1A1A2E]">{data.vorname}</h2>
              {data.position && <p className="text-sm text-[#6B7280]">{data.position}</p>}
            </div>
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full"
              style={{ background: data.bg_farbe || '#EBF4FD', color: data.farbe || '#338edc' }}>
              {data.status_label || statusLabel(status)}
            </span>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-3">{t('bewerberstatus.eingegangenAm', 'Bewerbung eingegangen am {{datum}}', { datum: formatDate(data.beworben_am) })}</p>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{
                width: `${data.progress || 0}%`,
                background: data.farbe || '#338edc',
              }} />
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1.5">{t('bewerberstatus.fortschrittProzent', '{{prozent}}% abgeschlossen', { prozent: data.progress || 0 })}</p>
          </div>
        </div>

        {/* Status Progress Stepper */}
        <div className="bg-white rounded-xl border border-[var(--border-default,#E5E7EB)] p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#4B5563] mb-4">{t('bewerberstatus.fortschritt', 'Dein Fortschritt')}</h3>
          <div className="space-y-0">
            {stepperStatus.map((stKey, i) => {
              const isDone = !isTerminal && currentIdx >= 0 && i < currentIdx;
              const isCurrent = !isTerminal && i === currentIdx;
              const isLast = i === stepperStatus.length - 1;
              return (
                <div key={stKey} className="flex gap-3 relative">
                  {!isLast && (
                    <div className="absolute left-[13px] top-[28px] bottom-0 w-0.5"
                      style={{ background: isDone ? '#10B981' : '#E5E7EB' }} />
                  )}
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      background: isDone ? '#10B981' : isCurrent ? (data.farbe || '#338edc') : '#F3F4F6',
                      border: `2px solid ${isDone ? '#10B981' : isCurrent ? (data.farbe || '#338edc') : '#E5E7EB'}`,
                      color: isDone || isCurrent ? 'white' : '#9CA3AF',
                    }}>
                    {isDone ? <IconCheck /> : <span className="text-[9px] font-bold">{i + 1}</span>}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="text-sm font-medium" style={{
                      color: isDone ? '#4B5563' : isCurrent ? (data.farbe || '#338edc') : '#9CA3AF'
                    }}>
                      {statusLabel(stKey)}
                    </p>
                    {isCurrent && (
                      <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mt-1"
                        style={{ background: data.bg_farbe || '#EBF4FD', color: data.farbe || '#338edc' }}>
                        {t('bewerberstatus.aktuellerSchritt', 'Aktueller Schritt')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Terminal-Status Banner (abgelehnt/inaktiv) */}
          {isTerminal && (
            <div className="mt-4 p-3 rounded-lg flex items-center gap-3"
              style={{ background: data.bg_farbe || '#FEF2F2', color: data.farbe || '#EF4444' }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: data.farbe || '#EF4444', color: 'white' }}>
                <IconX />
              </div>
              <p className="text-sm font-semibold">{data.status_label || statusLabel(status)}</p>
            </div>
          )}
        </div>

        {/* Timeline Events */}
        {data.timeline?.length > 0 && (
          <div className="bg-white rounded-xl border border-[var(--border-default,#E5E7EB)] p-5">
            <h3 className="text-sm font-semibold text-[#4B5563] mb-4">{t('bewerberstatus.zeitverlauf', 'Zeitverlauf')}</h3>
            <div className="space-y-3">
              {data.timeline.map((ev, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#338edc' }} />
                  <div>
                    <p className="text-sm text-[#1A1A2E]">{ev.typ}</p>
                    <p className="text-xs text-[#9CA3AF]">{formatDate(ev.am)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-xs text-center text-[#9CA3AF] mt-8">
          {t('bewerberstatus.kontakt', 'Bei Fragen kontaktiere uns unter')}{' '}
          <a href="mailto:hr@weship.eu" className="text-[#338edc]">hr@weship.eu</a>
        </p>
      </div>
    </div>
  );
}
